<template>
	<div
		class="login-page prefilled-page iframe"
		v-if="theme"
	>
		<LoaderComponent
			v-if="isLoading"
			:style="{ color: theme.altTextColor }"
		/>
		<LoginError
			:theme="theme"
			:language="fimenu.language"
			v-else-if="creatingError"
		/>
		<LoginSuccess
			:theme="theme"
			:fimenu="fimenu"
			v-else-if="creatingSuccess"
			:sourceData="getSourceData()"
			:customerApp="customerApp"
		/>
		<!-- Error codes are meant for debugging. So we may see where the customer came from. -->
		<div
			v-else-if="!!errorMessage && !storeCode"
			style="max-width: 500px"
		>
			<span>An error has occurred (#{{ errorMessage }})</span>
			<br />
			<br />

			<p>Please try accessing the application again from the original link. We apologize for any inconvenience and appreciate your understanding.</p>
		</div>

		<div
			v-else-if="!storeCode"
			style="max-width: 500px"
		>
			<span>An unexpected error has occurred</span>
			<br />
			<br />

			<p>Please try accessing the application again from the original link. We apologize for any inconvenience and appreciate your understanding.</p>
		</div>
		<main v-if="!isLoading && !creatingError && !creatingSuccess && storeCode">
			<button
				class="language-change"
				@click="languageChange"
			>
				{{ localizationOposite }} <i class="fas fa-globe"></i>
			</button>
			<!--<button @click="fillData" class="autofill-btn"><p>Auto-Fill</p></button>-->
			<!--<h1 :style="{color: theme.altTextColor}">{{localization.creditApp.creationPage.applicantInfo[fimenu.language]}}</h1>-->
			<div class="form">
				<div class="form-group">
					<InputTextbox
						v-model:saturn="firstName"
						:invalid="v$.$dirty && v$.firstName.$invalid"
						:label="localization.creditApp.creationPage.firstName[fimenu.language]"
						:showIcon="false"
					/>
					<span
						class="textbox-error-message"
						v-if="v$.$dirty && v$.firstName.required.$invalid"
						>{{ requiredMessage }}</span
					>
					<span
						class="textbox-error-message"
						v-else-if="v$.$dirty && v$.firstName.validName.$invalid"
					>
						{{ localization.creditApp.creationPage.nameValidationMessage[fimenu.language] }}
					</span>
				</div>
				<div class="form-group">
					<InputTextbox
						v-model:saturn="lastName"
						:invalid="v$.$dirty && v$.lastName.$invalid"
						:label="localization.creditApp.creationPage.lastName[fimenu.language]"
						:showIcon="false"
					/>
					<span
						v-if="v$.$dirty && v$.lastName.required.$invalid"
						class="textbox-error-message"
						>{{ requiredMessage }}</span
					>
					<span
						v-else-if="v$.$dirty && v$.lastName.validName.$invalid"
						class="textbox-error-message"
						>{{ localization.creditApp.creationPage.nameValidationMessage[fimenu.language] }}</span
					>
				</div>
				<div class="form-group">
					<InputTextbox
						type="Email"
						v-model:saturn="emailAddress"
						:invalid="v$.$dirty && v$.emailAddress.$invalid"
						:label="localization.creditApp.creationPage.emailAddress[fimenu.language]"
						:showIcon="false"
					/>
					<span
						v-if="v$.$dirty && v$.emailAddress.required.$invalid"
						class="textbox-error-message"
						>{{ requiredMessage }}</span
					>
					<span
						v-else-if="v$.$dirty && v$.emailAddress.email.$invalid"
						class="textbox-error-message"
					>
						Email format should be:
						<span class="error-emphasis">someone@email.com</span></span
					>
				</div>
				<div class="form-group">
					<InputPhone
						type="tel"
						v-model:saturn="phoneNumber"
						:label="localization.creditApp.creationPage.phoneNumber[fimenu.language]"
						:showIcon="false"
						:invalid="v$.$dirty && v$.phoneNumber.$invalid"
						minlength="10"
					/>
					<span
						v-if="v$.$dirty && v$.phoneNumber.required.$invalid"
						class="textbox-error-message"
						>{{ requiredMessage }}</span
					>
				</div>
				<div class="form-group">
					<InputTextDate
						v-model:saturn="dateOfBirth"
						style="width: 100%"
						disableCalendarPopup
						:label="localization.creditApp.creationPage.dateOfBirth[fimenu.language]"
						:invalid="v$.$dirty && v$.dateOfBirth.$invalid"
						:showIcon="false"
					/>
					<span
						v-if="v$.$dirty && v$.dateOfBirth.required.$invalid"
						class="textbox-error-message"
					>
						{{ requiredMessage }}
					</span>
					<span
						v-else-if="v$.$dirty && v$.dateOfBirth.validDate.$invalid"
						class="textbox-error-message"
					>
						{{ localization.creditApp.creationPage.dateOfBirthFormatMessage[fimenu.language] }}
					</span>
					<span
						v-else-if="v$.$dirty && v$.dateOfBirth.validDateOfBirth.$invalid"
						class="textbox-error-message"
					>
						{{ localization.creditApp.creationPage.dateOfBirthUnderageMessage[fimenu.language] }}
					</span>
				</div>
			</div>
			<div class="legal-term-container">
				<div
					class="legal-term"
					@click="handleTermsCheckbox(getRelevantTerm(privacyNoticeType))"
				>
					<button
						class="terms-checkbox-button"
						:class="`${!isPrivacyPolicyChecked && v$.$dirty ? 'invalid' : ''}`"
					>
						<i
							:class="{
								'far fa-check-square': isPrivacyPolicyChecked,
								'far fa-square': !isPrivacyPolicyChecked,
							}"
						></i>
					</button>
					<span
						>{{ localization.creditApp.creationPage.iAcknowledge[fimenu.language] }}
						<button
							@click.stop="openPrivacyPolicyModal"
							class="underline"
						>
							{{ localization.creditApp.creationPage.privacyPolicy[fimenu.language] }}
						</button>
					</span>
				</div>
				<div
					class="legal-term"
					@click="handleTermsCheckbox(getRelevantTerm(smsConsentType))"
				>
					<button
						class="terms-checkbox-button"
						:class="`${!isSMSConsentChecked && v$.$dirty ? 'invalid' : ''}`"
					>
						<i
							:class="{
								'far fa-check-square': isSMSConsentChecked,
								'far fa-square': !isSMSConsentChecked,
							}"
						></i>
					</button>
					<span>
						{{ localization.creditApp.creationPage.iAcknowledge[fimenu.language] }}
						<a
							@click.stop.prevent="openSMSConsentModal"
							class="underline"
							href="#"
						>
							{{ localization.creditApp.creationPage.smsConsentButton[fimenu.language] }} </a
						>,
						{{ localization.creditApp.creationPage.smsConsentText[fimenu.language] }}
					</span>
				</div>
			</div>
			<button
				class="button-full"
				id="create-application-button"
				ref="createAppButton"
				:style="{
					backgroundColor: theme.successButtonColor,
					color: theme.successButtonTextColor,
				}"
				@click="sendApplication"
			>
				{{ localization.creditApp.creationPage.createApplication[fimenu.language] }}
			</button>
		</main>
	</div>
</template>

<script>
	import { email, maxLength, minLength, numeric, required } from '@vuelidate/validators';
	import { validDate, validDateOfBirth, validName } from '@core/services/custom-validators';
	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import ApplicationsSettingsLoader from '@core/classes/Applications/ApplicationsSettingsLoader';
	import ENUMS from '@core/classes/Enums';
	import InputPhone from '@core/components/InputPhone.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import InputTextDate from '@core/components/InputDate.vue';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import LOCALIZATION from '@core/classes/Localization';
	import LoginError from '@/components/customerApp/LoginError.vue';
	import LoginSuccess from '@/components/customerApp/LoginSuccess.vue';
	import LogRocket from 'logrocket';
	import modalLegalTerm from '@core/modals/modalLegalTerm.vue';
	import moment from 'moment';
	import settings from 'settings';
	import { useVuelidate } from '@vuelidate/core';
	import util from '@core/services/util';

	export default {
		name: 'CreditAppCreateApplication',
		props: ['fimenu', 'sendHTMLToManager'],
		setup() {
			return { v$: useVuelidate({ $scope: false }) };
		},
		data() {
			return {
				customerApp: null,
				theme: null,
				creatingError: false,
				creatingSuccess: false,
				customerPrefilledData: null,
				isLoading: true,
				firstName: null,
				lastName: null,
				dateOfBirth: null,
				emailAddress: null,
				phoneNumber: null,
				pageInitialHeight: null,
				pageInitialWidth: null,
				storeCode: null,
				termsList: null,
				termsData: null,
				isPrivacyPolicyChecked: null,
				isSMSConsentChecked: null,
			};
		},
		mounted() {
			const viewport = document.querySelector('meta[name=viewport]');
			viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');
			this.pageInitialHeight = window.innerHeight;
			this.pageInitialWidth = window.innerWidth;
			if (this.$route.query?.prefilledData) {
				const decodedPrefilledData = JSON.parse(atob(this.$route.query.prefilledData));
				this.firstName = decodedPrefilledData?.firstName;
				this.lastName = decodedPrefilledData?.lastName;
				this.emailAddress = decodedPrefilledData?.email;
				this.phoneNumber = decodedPrefilledData?.phone;
			}
			window.addEventListener('resize', this.onResizeHandler);
		},
		computed: {
			localizationOposite() {
				if (this.fimenu.language == 'Spanish') {
					return 'English';
				} else {
					return 'Español';
				}
			},
			localization() {
				return LOCALIZATION;
			},
			util() {
				return util;
			},
			ENUMS() {
				return ENUMS;
			},
			privacyNoticeType() {
				return ENUMS.LegalTermTypes.PRIVACY_NOTICE;
			},
			smsConsentType() {
				return ENUMS.LegalTermTypes.SMS_CONSENT;
			},
			languageOptions() {
				return ENUMS.LANGUAGE_DISPLAY;
			},
			hasAgreedToTerms() {
				return this.termsList?.every(term => term.isChecked);
			},
			errorMessage() {
				return this.$route.query?.errorCode;
			},
			requiredMessage() {
				return this.localization.creditApp.creationPage.requiredMessage[this.fimenu.language];
			},
		},
		async created() {
			this.fimenu.language = 'English';
			if (!this.$route.query.storeCode) return this.$router.push(`/creditapp?errorCode=OTPPageNoStoreCode`);
			if (this.$route.query.leadSource && this.$route.query.storeCode) {
				//GET STORE INFO (DETAILS)
				const response = await api.settings.getStoreBasicInfo(this.$route.query.storeCode);
				if (response.data) {
					if (response.data.theme.mainTextColor) {
						if (response.data.storeFiles !== null && response.data.storeFiles.length > 0) {
							const { filepath, filetype } = response.data.storeFiles.find(f => f.filetype === ENUMS.STORE_FILE_TYPES.MAIN_LOGO);
							response.data.theme.logoSrc = filepath
								? `${settings.apiUrl}/Settings/GetStoreMainLogo?storeCode=${this.$route.query.storeCode}&ver=${Date.now()}`
								: null;
						}
					} else {
						response.data.theme = ApplicationsSettingsLoader.ApplicationSettings.theme;
					}

					this.fimenu.storeInfo = response.data;
					this.theme = this.fimenu.storeInfo.theme;
					this.storeCode = this.$route.query.storeCode;
				}

				this.isLoading = false;
			} else {
				this.isLoading = false;
			}

			localStorage.removeItem('applicationObject');
			localStorage.removeItem('fimenuObject');

			await this.getLegalTerms();
		},
		validations() {
			const validations = {
				firstName: { required, validName },
				lastName: { required, validName },
				dateOfBirth: {
					required,
					validDate,
					validDateOfBirth,
					minLength: minLength(10),
				},
				emailAddress: { required, email },
				phoneNumber: {
					required,
					numeric,
					minLength: minLength(10),
					maxLength: maxLength(10),
				},
			};
			return validations;
		},
		methods: {
			handleInput(value, variable) {
				this[variable] = value;
			},
			languageChange() {
				if (this.fimenu.language == 'English') {
					this.fimenu.language = 'Spanish';
				} else {
					this.fimenu.language = 'English';
				}
				this.createTermsList();
			},
			fillData() {
				this.firstName = 'Test';
				this.lastName = 'Testing';
				this.dateOfBirth = moment('01/01/1991', 'MM/DD/YYYY').format('YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]');
				this.emailAddress = 'gretelalt01@gmail.com';
				this.phoneNumber = '3059798924';
			},
			getSourceData() {
				const sourceData = {
					language: this.fimenu.language,
					firstName: this.firstName,
					lastName: this.lastName,
					dateOfBirth: this.dateOfBirth,
					phoneNumber: this.phoneNumber,
					emailAddress: this.emailAddress,
					storeCode: this.storeCode,
					storeName: this.fimenu.storeInfo.storeName,
					storePhone: this.fimenu.storeInfo.storePhone,
				};
				return sourceData;
			},
			sendApplication() {
				this.$refs.createAppButton.classList.add('clicked');
				setTimeout(() => {
					this.$refs.createAppButton?.classList.remove('clicked');
				}, 400);

				if (this.v$.$invalid || !this.hasAgreedToTerms) {
					this.v$.$touch();
					return;
				}
				this.isLoading = true;
				const prefilledData = {
					language: this.fimenu.language,
					firstName: this.firstName,
					lastName: this.lastName,
					dateOfBirth: moment(this.dateOfBirth, 'MM/DD/YYYY').format('YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]'),
					phoneNumber: this.phoneNumber,
					emailAddress: this.emailAddress,
					storeCode: this.storeCode,
				};
				this.sourceData = prefilledData;

				const acceptedTermsDate = util.toMomentUtc(new Date());
				const acceptedTermsDateOffset = util.toMoment(new Date()).utcOffset();

				api.applications
					.createApplication({
						isInStore: this.$route.query.isInStore,
						prefilledData,
						acceptedTerms: this.termsList.map(t => ({
							id: t.id,
							termsType: t.termsType,
							termsLabel: t.termsLabel,
							termsText: t.termsText,
						})),
						userDateTime: acceptedTermsDate,
						userDateTimeOffset: acceptedTermsDateOffset,
						createAppParams: this.$route.query,
					})
					.then(response => {
						// console.log(response)

						if (response.data) {
							this.customerApp = response.data.application;
							this.creatingSuccess = true;

							LogRocket.track('Customer_App_Creation', {
								storeCode: this.storeCode,
								email: this.emailAddress,
								environment: settings.environmentName,
							});

							LogRocket.track(`Lead_Created`, {
								leadId: response.data.leadId ?? '',
								customerCell: this.phoneNumber,
								customerEmail: this.emailAddress,
								customerDob: this.dateOfBirth,
								storeName: this.fimenu.storeInfo.storeName,
								environment: settings.environmentName,
							});
						} else {
							this.creatingError = true;
						}
					})
					.catch(error => {
						this.creatingError = true;
						LogRocket.log('error creating customer', error);
					})
					.finally(() => {
						this.isLoading = false;
						if (this.creatingError) {
							LogRocket.track('Customer_App_Creation', {
								storeCode: this.storeCode,
								email: this.emailAddress,
								environment: settings.environmentName,
							});
						}
					});
			},
			onResizeHandler() {
				const metaViewport = document.querySelector('meta[name=viewport]');

				if (window.innerHeight < this.pageInitialHeight) {
					// document.documentElement.style.setProperty('overflow', 'auto')
					metaViewport.setAttribute('content', 'height=' + this.pageInitialHeight + 'px,width=device-width,initial-scale=1.0');
				} else {
					metaViewport.setAttribute('content', 'width=device-width,initial-scale=1.0,maximum-scale=1.0,user-scalable=0');
				}
			},
			getEnumDisplayValue(data, options) {
				const valueToMatch = options.find(term => term.value === data);
				return valueToMatch.display;
			},
			getRelevantTerm(termsType) {
				return this.termsList?.find(term => term.termsType === termsType);
			},
			async getLegalTerms() {
				const getLatestVersion = true;

				const termsToFetch = [this.privacyNoticeType, this.smsConsentType];

				await api.settings
					.getLegalTermsByType(getLatestVersion, termsToFetch)
					.then(response => {
						this.termsData = response.data;
					})
					// eslint-disable-next-line no-console
					.catch(err => console.log('err', err));

				this.createTermsList();
			},
			createTermsList() {
				this.termsList = this.termsData.map(term => {
					const languageMatch = term.nameTranslations.find(nt => this.getEnumDisplayValue(nt.language, this.languageOptions) === this.fimenu.language);
					return {
						id: term.id,
						dateCreated: term.dateCreated,
						termsType: term.termsType,
						termsText: term.termsType === 0 ? languageMatch.termsText + this.lenderListText : languageMatch.termsText,
						language: this.getEnumDisplayValue(languageMatch.language, this.languageOptions),
						termsLabel: languageMatch.termsLabel,
						isChecked: false,
					};
				});

				this.isPrivacyPolicyChecked = false;
				this.isSMSConsentChecked = false;
			},
			handleTermsCheckbox(item) {
				if (item.termsType === this.privacyNoticeType) {
					this.isPrivacyPolicyChecked = !this.isPrivacyPolicyChecked;
				} else if (item.termsType === this.smsConsentType) {
					this.isSMSConsentChecked = !this.isSMSConsentChecked;
				}
				this.termsList = this.termsList.map(term => {
					if (term.id === item.id) {
						return { ...term, isChecked: !term.isChecked };
					} else {
						return term;
					}
				});
			},
			openPrivacyPolicyModal() {
				$modal.open(modalLegalTerm, {
					name: 'modalLegalTerm',
					passedData: {
						title: 'Privacy Policy',
						term: this.getRelevantTerm(this.privacyNoticeType),
					},
					backdrop: true,
				});
			},
			openSMSConsentModal() {
				$modal.open(modalLegalTerm, {
					name: 'modalLegalTerm',
					passedData: {
						title: 'SMS Terms and Conditions',
						term: this.getRelevantTerm(this.smsConsentType),
					},
					backdrop: true,
				});
			},
		},
		components: {
			InputTextbox,
			LoaderComponent,
			InputTextDate,
			LoginSuccess,
			LoginError,
			InputPhone,
			//modalPrivacyPolicy
			//AccordionList,
		},
	};
</script>
<style>
	.prefilled-page main,
	.prefilled-page main .saturn-textbox,
	.prefilled-page main .saturn-textbox input {
		background-color: var(--secondary-color);
	}

	.prefilled-page .autofill-btn {
		padding: 5px 10px;
		color: var(--swiper-theme-color);
		background: white;
		border: 1px solid var(--swiper-theme-color);
		border-radius: 5px;
	}

	.prefilled-page .autofill-btn:hover {
		background: var(--swiper-theme-color);
		color: white;
	}

	/*.form button {
        position: absolute;
        bottom: 15%;
        width: 80%;
        left: 50%;
        transform: translateX(-50%);
    }*/

	.prefilled-page .input-component {
		position: relative;
		display: inline-flex;
		flex-direction: column;
		align-items: center;
		min-width: 0;
		padding: 0;
		border: 0;
		vertical-align: top;
		width: 100%;
	}

	.prefilled-page .input-component select {
		border: none !important;
	}

	.prefilled-page .input-component .saturn-textbox:has(.saturn-icon) input {
		border-bottom-right-radius: unset;
		border-top-right-radius: unset;
		padding-right: 0px;
	}

	.input-component.invalid fieldset.floatingLabel,
	.prefilled-page .input-component.invalid .saturn-icon {
		border: 1px solid var(--error-color) !important;
		color: var(--error-color) !important;
	}

	.input-component.invalid label.label-root,
	.prefilled-page fieldset:disabled .input-component.invalid label.label-root {
		color: var(--error-color);
	}

	.prefilled-page .input-component.clearable input {
		padding: 10px 25px 10px 14px;
	}

	.prefilled-page .input-component.dropdown select {
		padding: 10px;
	}

	.prefilled-page fieldset:disabled .input-component button.clear-input {
		display: none;
	}

	.prefilled-page fieldset:disabled .input-component.dropdown > * {
		pointer-events: none;
	}

	.prefilled-page .input-component button.clear-input {
		border: none;
		position: absolute;
		right: 35px;
		width: unset;
		height: unset;
		color: unset;
		padding: 5px !important;
		background: none !important;
		box-shadow: none !important;
	}

	.prefilled-page .input-component button.clear-input.noIcon-padding {
		right: 5px;
	}

	.prefilled-page .input-component button.clear-input i {
		transition: all 0.2s ease-out;
	}

	.prefilled-page .input-component button.clear-input:hover i {
		color: var(--error-color);
	}

	.prefilled-page .input-component.invalid .saturn-icon {
		border-left: none !important;
	}

	.input-component.focused fieldset.floatingLabel,
	.prefilled-page .input-component.focused .saturn-icon {
		border: 1px solid var(--main-color) !important;
		color: var(--main-color) !important;
	}

	.prefilled-page .input-component.focused label.label-root {
		color: var(--main-color);
	}

	.input-component label.label-root,
	.prefilled-page fieldset:disabled .input-component label.label-root {
		color: rgba(0, 0, 0, 0.6);
		font-size: 1.2rem;
		line-height: 1.4375em;
		letter-spacing: 0.00938em;
		padding: 0;
		display: block;
		transform-origin: top left;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 98%;
		position: absolute;
		left: 0;
		top: 0;
		z-index: 1;
		pointer-events: none;
		user-select: none;
		backdrop-filter: blur(4px);
		transform: translate(14px, -9px) scale(0.75);
		transition: all 200ms cubic-bezier(0, 0, 0.2, 1);
	}

	.prefilled-page .input-component.left-icon label.label-root {
		left: 36px;
		transform: translate(6px, -9px) scale(0.75);
		max-width: calc(98% - 55px);
	}

	fieldset:disabled .input-component.left-icon label.label-root,
	.prefilled-page fieldset:disabled .input-component.left-icon.empty:not(.focused) label.label-root {
		left: 0;
		transform: translate(13px, -9px) scale(0.75);
		max-width: calc(98% - 35px);
	}

	.prefilled-page .input-component.empty:not(.focused) label.label-root {
		max-width: 65%;
		top: 50%;
		transform: translate(12px, -50%) scale(0.95);
		z-index: 1;
		backdrop-filter: blur(2px);
	}

	.prefilled-page .input-component.left-icon.empty:not(.focused) label.label-root {
		transform: translate(5px, -50%) scale(0.95);
		max-width: calc(100% - 45px);
	}

	.prefilled-page fieldset:disabled .input-component.empty:not(.focused) label.label-root {
		max-width: 98%;
		width: unset;
		top: 0;
		transform: translate(14px, -9px) scale(0.75);
		z-index: 2;
	}

	fieldset:disabled .input-component label.label-root,
	.input-component:has(input:disabled) label.label-root,
	.input-component:has(textarea:disabled) label.label-root,
	.prefilled-page .input-component:has(select:disabled) label.label-root {
		font-weight: 800;
	}

	.prefilled-page .input-component .saturn-textbox {
		font-size: 1rem;
		line-height: 1.4375em;
		letter-spacing: 0.00938em;
		color: rgba(0, 0, 0, 0.87);
		box-sizing: border-box;
		cursor: text;
		display: inline-flex;
		-webkit-box-align: center;
		align-items: center;
		position: relative;
		border-radius: 4px;
		/*height: calc(1.475em + 30px); */
		height: calc(1.8rem + 30px);
		width: 100%;
		/*max-height: 40px;*/
		/*z-index: 1;
        overflow: hidden;*/
	}

	.prefilled-page .input-component .saturn-textbox fieldset.floatingLabel {
		position: absolute;
		box-sizing: border-box;
		border: 1px solid rgba(0, 0, 0, 0.23);
		border-radius: 4px;
		top: -5px;
		bottom: 0;
		left: 0;
		width: 100%;
		padding: 0 8px;
		margin: 0;
		height: unset !important;
		pointer-events: none;
		transition: all 200ms cubic-bezier(0, 0, 0.2, 1);
	}

	.prefilled-page .input-component.left-icon .saturn-textbox fieldset.floatingLabel {
		padding: 0 10% 0 36px;
	}

	.prefilled-page fieldset:disabled .input-component.left-icon .saturn-textbox fieldset.floatingLabel {
		padding: 0 0 0 8px;
	}

	.prefilled-page .input-component .saturn-textbox fieldset.floatingLabel legend.label-with-infoicon-containter {
		float: unset;
		width: auto;
		overflow: hidden;
		display: block;
		padding: 0;
		height: 11px;
		font-size: 0.75em;
		visibility: hidden;
		transition: max-width 50ms cubic-bezier(0, 0, 0.2, 1) 0ms;
		max-width: 85%;
		text-align: left;
	}

	.prefilled-page .input-component.empty .saturn-textbox fieldset.floatingLabel legend.label-with-infoicon-containter {
		max-width: 0.01px;
	}

	.prefilled-page fieldset:disabled .input-component.empty .saturn-textbox fieldset.floatingLabel legend.label-with-infoicon-containter {
		max-width: 85%;
	}

	.prefilled-page .input-component .saturn-textbox fieldset.floatingLabel legend.label-with-infoicon-containter span {
		padding-left: 5px;
		padding-right: 5px;
		display: inline-block;
		opacity: 0;
		visibility: visible;
	}

	.prefilled-page .input-component .saturn-textbox .saturn-icon {
		box-sizing: border-box;
		height: 100%;
		min-width: 34px;
		display: flex;
		justify-content: center;
		align-items: center;
		background-color: var(--input-component-saturn-icon-background-color);
		color: var(--input-component-background-color);
		font-size: 0.9rem;
		cursor: default;
		border: none !important;
		transition: all 200ms cubic-bezier(0, 0, 0.2, 1);
	}

	.prefilled-page .input-component.left-icon .saturn-textbox .saturn-icon {
		border-top-left-radius: 4px;
		border-bottom-left-radius: 4px;
	}

	.prefilled-page .input-component.left-icon .saturn-textbox .action-slot {
		right: unset;
		left: 3px;
	}

	.prefilled-page .input-component.action.left-icon .saturn-textbox:has(.action-slot) input {
		padding-left: 42px;
		border-top-left-radius: 4px;
		border-bottom-left-radius: 4px;
	}

	.input-component.left-icon .saturn-textbox input,
	.prefilled-page .input-component.left-icon .saturn-textbox select {
		padding: 10px 5px;
		border-top-left-radius: unset;
		border-bottom-left-radius: unset;
		border-top-right-radius: 4px !important;
		border-bottom-right-radius: 4px !important;
	}

	.InputCurrency .input-component.left-icon .saturn-textbox input,
	.prefilled-page .InputNumber .input-component.left-icon .saturn-textbox input {
		text-align: end;
	}

	.prefilled-page .input-component:not(.left-icon) .saturn-textbox .saturn-icon {
		border-top-right-radius: 4px;
		border-bottom-right-radius: 4px;
	}

	.prefilled-page .input-component.dropdown .saturn-textbox .saturn-icon {
		position: absolute;
		right: 0;
		top: 0;
		pointer-events: none;
	}

	.prefilled-page .input-component.dropdown .saturn-textbox .saturn-icon {
		background: unset !important;
		border: unset !important;
	}

	.prefilled-page .input-component.dropdown .saturn-textbox .saturn-icon i {
		margin-top: -4px;
		color: var(--text-color);
	}

	.prefilled-page .input-component.dropdown.invalid .saturn-textbox .saturn-icon i {
		margin-top: -4px;
		color: var(--error-color);
	}

	.prefilled-page .input-component.dropdown.focused .saturn-textbox .saturn-icon i {
		color: var(--main-color);
	}

	.prefilled-page .input-component.dropdown.focused .saturn-textbox .saturn-icon {
		transform: rotate(180deg);
	}

	.prefilled-page .input-component .saturn-textbox-error-message {
		display: none;
	}

	.prefilled-page .textbox-error-message {
		display: block;
		top: 100%;
		left: 2px;
		background-color: var(--secondary-color);
		color: var(--error-color);
		font-size: 0.85rem;
		animation: expandable 0.1s ease-out forwards;
		z-index: 1000;
		align-self: flex-start;
	}
	.prefilled-page .textbox-error-message .error-emphasis {
		font-size: 0.85rem;
		font-style: italic;
	}

	.prefilled-page .input-component .defaultIconClass {
		color: var(--main-color);
		position: absolute;
		top: -6px;
		right: -6px;
		z-index: 999;
	}

	.prefilled-page .input-component .saturn-textbox textarea {
		font-family: roboto;
		font-size: 12pt;
		padding: 15px;
		box-sizing: border-box;
		resize: none;
		border: none;
		height: 100%;
		width: 100%;
	}

	.prefilled-page .input-component .saturn-textbox:has(textarea) {
		aspect-ratio: 1 / 0.75;
	}

	.prefilled-page .input-component.multi-line.empty:not(.focused) label.label-root {
		top: 15px;
		transform: translate(12px, 0) scale(0.95);
	}

	.prefilled-page .input-component.action input {
		padding-right: 40px;
	}

	.prefilled-page .action-slot {
		position: absolute;
		right: 2px;
		top: 50%;
		transform: translateY(-50%);
	}

	.prefilled-page .action-slot button {
		height: 35px;
		width: 35px;
		padding: unset;
		border-radius: 4px;
		position: relative;
	}

	.prefilled-page .action-slot button i {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	.prefilled-page .input-component .skeleton.match input {
		border-left: 0;
	}

	.prefilled-page .input-component .skeleton .cdk .cdk-textbox {
		width: 100%;
	}

	.prefilled-page .input-component .skeleton.icon-on-left .cdk .cdk-icon {
		right: unset;
		/*
            left: 7px;*/
	}

	/* InputCheckbox
       ----------------------------- */

	.prefilled-page .input-checkbox {
		display: flex;
		align-items: center;
		padding: 5px 0px;
		cursor: pointer;
	}

	.prefilled-page .input-checkbox .ic-label {
		margin-left: 10px;
	}

	.prefilled-page .input-checkbox .disabled {
		color: var(--disabled-color);
		cursor: not-allowed;
	}

	/* InputRadio
       ----------------------------- */

	.prefilled-page .radio-button-inline .gap {
		gap: 5px;
	}

	/* InputDate
       ----------------------------- */

	.prefilled-page .vdpPeriodControl button {
		color: var(--input-datepicker-button-color-text);
		font-weight: bold;
		height: auto;
	}

	.prefilled-page .vdpCell.selectable.today {
		color: var(--error-color);
		font-weight: 500;
		padding: 0 !important;
		border: 3px solid var(--error-color) !important;
		border-radius: 50%;
	}

	.prefilled-page .vdpTable thead {
		background: none !important;
	}

	.prefilled-page .vdpTable thead th {
		padding: 0.3em 0.5em 1.8em !important;
		border: none !important;
	}

	.prefilled-page .vdpTable tbody tr {
		background: none !important;
	}

	.prefilled-page .vdpTable td {
		padding: 0.5em 0 !important;
		border: none !important;
	}

	.prefilled-page fieldset {
		border: none;
	}

	.prefilled-page .form {
		margin-top: 12px;
		row-gap: 20px;
	}

	.prefilled-page .terms-checkbox-button.invalid i {
		color: var(--error-color);
	}

	.prefilled-page .clicked {
		background-color: rgb(33 138 11) !important;
	}
</style>
